import React, { useEffect, useMemo, useState } from "react";
import Gmap from "./Gmap";
import {
  Search,
  MapPin,
  ChevronRight,
  Loader2,
  Landmark,
  UtensilsCrossed,
  BedDouble,
  Compass,
  X,
  SlidersHorizontal,
  Heart,
  Layers,
} from "lucide-react";
import { hasUsableImageUrl } from "../lib/placeholderImages";
import { normalizeLocationMapMedia } from "../lib/assetUrl";
import { formatLocationAddress, hasFormattedAddress, locationAddressSearchText } from "../lib/locationAddress";

/** Danh mục lớn — khớp `location.category` (culture | food | stay) */
const LEGACY_BY_MAIN = {
  "du-lich-van-hoa": "culture",
  "am-thuc-local": "food",
  "homestay-khach-san": "stay",
};

const MAIN_CATEGORIES = [
  { slug: "all", label: "Tất cả", icon: Compass },
  { slug: "du-lich-van-hoa", label: "VĂN HOÁ", icon: Landmark },
  { slug: "am-thuc-local", label: "ĂN/UỐNG", icon: UtensilsCrossed },
  { slug: "homestay-khach-san", label: "HOMESTAY", icon: BedDouble },
];

const SUBTYPE_GROUPS = [
  {
    main: "du-lich-van-hoa",
    label: "Văn hoá",
    items: [
      { value: "du-lich", label: "Điểm du lịch" },
      { value: "chua", label: "Chùa" },
      { value: "nha-tho", label: "Nhà thờ" },
      { value: "di-tich", label: "Di tích" },
    ],
  },
  {
    main: "am-thuc-local",
    label: "Ăn / uống local",
    items: [
      { value: "ca-phe-local", label: "Cà phê local" },
      { value: "ca-phe-co-view", label: "Cà phê có view" },
      { value: "quan-lau-nuong", label: "Lẩu nướng" },
      { value: "quan-com-gia-dinh", label: "Cơm gia đình" },
      { value: "quan-an-no", label: "Ăn no" },
      { value: "quan-an-vat", label: "Ăn vặt" },
    ],
  },
  {
    main: "homestay-khach-san",
    label: "Homestay",
    items: [
      { value: "home-co-view", label: "Home có view" },
      { value: "home-tien-nghi", label: "Home tiện nghi" },
      { value: "khach-san", label: "Khách sạn" },
    ],
  },
];

function slugifySubtype(v) {
  return String(v || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function matchesMainCategory(loc, mainSlug) {
  if (mainSlug === "all") return true;
  const leg = LEGACY_BY_MAIN[mainSlug];
  return leg ? loc.category === leg : false;
}

const LocationCard = ({ item, onSelectCard, isFav, onFav }) => {
  const { image: cover, gallery: extras } = normalizeLocationMapMedia(item);
  const thumbSrc = hasUsableImageUrl(cover) ? cover : extras[0] || "";
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelectCard?.(item)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSelectCard?.(item);
        }
      }}
      className="group relative flex cursor-pointer gap-3 rounded-2xl border border-[#d9ccb4] bg-[#fffaf0] p-2.5 shadow-[0_4px_12px_rgba(80,65,40,0.06)] transition hover:-translate-y-0.5 hover:border-[#5f745f]/55 hover:bg-white hover:shadow-[0_14px_28px_rgba(80,65,40,0.13)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#5f745f]"
    >
      <div className="relative h-[72px] w-[72px] shrink-0 overflow-hidden rounded-xl border border-[#e4d8c2] bg-[#efe6d4]">
        {thumbSrc ? (
          <img
            src={thumbSrc}
            alt=""
            loading="lazy"
            className="h-full w-full object-cover transition duration-300 group-hover:scale-105"
            onError={(e) => {
              e.target.onerror = null;
              e.target.style.display = "none";
            }}
          />
        ) : (
          <span className="flex h-full w-full items-center justify-center text-[#b5a78c]">
            <MapPin size={20} />
          </span>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col justify-center pr-8">
        <p className="line-clamp-2 font-faustina text-[15px] font-bold leading-snug text-[#2f352c] transition group-hover:text-[#3f5340]">
          {item.name}
        </p>
        {hasFormattedAddress(item) && (
          <p className="mt-0.5 flex items-start gap-1 text-[11px] leading-snug text-[#7a7467]">
            <MapPin size={10} className="mt-0.5 shrink-0" />
            <span className="line-clamp-1">{formatLocationAddress(item)}</span>
          </p>
        )}
        {item.categoryName && (
          <span className="mt-1.5 inline-flex w-fit max-w-full truncate rounded-full border border-[#d6dfcb] bg-[#eef2e6] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#3f5340]">
            {item.categoryName}
          </span>
        )}
      </div>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onFav?.(item.id);
        }}
        className={`absolute right-2 top-2 flex h-8 w-8 items-center justify-center rounded-full border transition ${
          isFav
            ? "border-[#e8b9b3] bg-[#fdeeeb] text-[#a85d5d]"
            : "border-transparent bg-transparent text-[#a85d5d]/40 hover:border-[#e8b9b3] hover:bg-[#fdf3f1] hover:text-[#a85d5d]"
        }`}
        title={isFav ? "Bỏ yêu thích" : "Yêu thích"}
        aria-label={isFav ? "Bỏ yêu thích" : "Yêu thích"}
        aria-pressed={!!isFav}
      >
        <Heart size={15} fill={isFav ? "currentColor" : "none"} />
      </button>
      <ChevronRight
        size={15}
        className="absolute bottom-2.5 right-3 text-[#5f745f]/40 transition group-hover:translate-x-0.5 group-hover:text-[#5f745f]"
      />
    </div>
  );
};

const LocationCardSkeleton = () => (
  <div className="flex animate-pulse gap-3 rounded-2xl border border-[#e4d8c2] bg-[#fffaf0] p-2.5">
    <div className="h-[72px] w-[72px] rounded-xl bg-[#efe6d4]" />
    <div className="flex-1 space-y-2 py-1">
      <div className="h-3.5 w-3/4 rounded bg-[#efe6d4]" />
      <div className="h-3 w-full rounded bg-[#f3ecdc]" />
      <div className="h-4 w-1/3 rounded-full bg-[#eef2e6]" />
    </div>
  </div>
);

const Chip = ({ active, onClick, children, className = "" }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] font-bold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#5f745f]/60 ${
      active
        ? "border-[#5f745f] bg-[#5f745f] text-white shadow-sm"
        : "border-[#d9ccb4] bg-[#fffaf0] text-[#5d6758] hover:border-[#5f745f]/50 hover:bg-white"
    } ${className}`}
  >
    {children}
  </button>
);

export default function HomeMapExplorer({
  locations = [],
  loading = false,
  error = "",
  toggleFavorite,
  isFavorite,
  introPhase,
  hideSidebar = false,
  preferDensestOnLoad = false,
  initialOnlyFavorites = false,
}) {
  const [search, setSearch] = useState("");
  const [mainSlug, setMainSlug] = useState("all");
  const [subSlug, setSubSlug] = useState("all");
  const [sidebarOpen, setSidebarOpen] = useState(!hideSidebar);
  const [onlyFavorites, setOnlyFavorites] = useState(Boolean(initialOnlyFavorites));
  const [flyToRequest, setFlyToRequest] = useState(null);

  useEffect(() => {
    setSidebarOpen(!hideSidebar);
  }, [hideSidebar]);

  useEffect(() => {
    setOnlyFavorites(Boolean(initialOnlyFavorites));
  }, [initialOnlyFavorites]);

  useEffect(() => {
    setSubSlug("all");
  }, [mainSlug]);

  // Deep-link support: /?locationId=<id>#home-hero
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search || "");
      const locationId = params.get("locationId");
      if (!locationId) return;
      setFlyToRequest({ locationId: String(locationId), nonce: Date.now(), openPopup: true });
      if (!hideSidebar) setSidebarOpen(false);
    } catch {
      // ignore
    }
  }, [hideSidebar]);

  const hasActiveFilter = Boolean(search.trim()) || mainSlug !== "all" || subSlug !== "all" || onlyFavorites;
  const resetFilters = () => {
    setSearch("");
    setMainSlug("all");
    setSubSlug("all");
    setOnlyFavorites(false);
  };

  const selectCardOnMap = (item) => {
    setFlyToRequest({ locationId: item.id, nonce: Date.now(), openPopup: true });
  };

  const subTypeOptions = useMemo(() => {
  if (mainSlug === "all") return [];

  const group = SUBTYPE_GROUPS.find((g) => g.main === mainSlug);

  return (group?.items || []).map((it) => ({
    slug: it.value,
    name: it.label,
  }));
}, [mainSlug]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    return locations.filter((loc) => {
      if (onlyFavorites && typeof isFavorite === "function" && !isFavorite(loc.id)) return false;
      if (!matchesMainCategory(loc, mainSlug)) return false;
      if (subSlug !== "all") {
      const leaf = slugifySubtype(loc.categorySlug);
      const selected = slugifySubtype(subSlug);
      if (leaf !== selected) return false;
    }
      if (!q) return true;
      return (
        loc.name?.toLowerCase().includes(q) ||
        locationAddressSearchText(loc).includes(q) ||
        (loc.categoryName && loc.categoryName.toLowerCase().includes(q))
      );
    });
  }, [locations, search, mainSlug, subSlug, onlyFavorites, isFavorite]);

  const mainCounts = useMemo(() => {
    const counts = { all: locations.length };
    MAIN_CATEGORIES.forEach(({ slug }) => {
      if (slug === "all") return;
      counts[slug] = locations.filter((l) => matchesMainCategory(l, slug)).length;
    });
    return counts;
  }, [locations]);

  return (
    <div className="absolute inset-0">
      <div className="flex h-full w-full overflow-hidden bg-transparent">
        {/* Sidebar */}
        {!hideSidebar && (
          <aside
            className={`z-20 flex flex-col border-r border-[#d9ccb4] bg-[#f7f1e6] shadow-[0_24px_60px_rgba(54,48,35,0.14)] transition-all duration-300 ${
              sidebarOpen ? "w-80 xl:w-[22rem]" : "w-0 overflow-hidden"
            }`}
            aria-label="Danh sách địa điểm"
          >
            {/* Header + search */}
            <div className="shrink-0 bg-[linear-gradient(180deg,#697961_0%,#556652_100%)] px-4 pb-4 pt-3.5 text-white">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-white/65">Khám phá</p>
                  <h2 className="font-faustina text-xl font-bold italic leading-tight text-[#fff9ef]">
                    Địa điểm Đà Lạt
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => setSidebarOpen(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-white/25 bg-white/10 text-white/85 transition hover:bg-white/20 hover:text-white"
                  aria-label="Đóng danh sách"
                  title="Đóng"
                >
                  <X size={15} />
                </button>
              </div>
              <label className="flex items-center gap-2 rounded-xl border border-white/25 bg-white/15 px-3 py-2.5 transition focus-within:border-white/60 focus-within:bg-white/20">
                <Search size={15} className="shrink-0 text-white/75" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Tìm tên, địa chỉ, loại hình..."
                  className="w-full appearance-none bg-transparent text-sm text-white outline-none placeholder:text-white/60 focus:outline-none focus:ring-0 [box-shadow:none]"
                />
                {search && (
                  <button type="button" onClick={() => setSearch("")} aria-label="Xoá tìm kiếm">
                    <X size={14} className="text-white/70 hover:text-white" />
                  </button>
                )}
              </label>
            </div>

            {/* Filters */}
            <div className="shrink-0 space-y-2.5 border-b border-[#d9ccb4] bg-[#fffaf0] px-4 py-3">
              <div className="flex gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                {MAIN_CATEGORIES.map((c) => {
                  const CIcon = c.icon || Layers;
                  return (
                    <Chip key={c.slug} active={c.slug === mainSlug} onClick={() => setMainSlug(c.slug)}>
                      <CIcon size={13} aria-hidden />
                      {c.label}
                      <span className={`font-semibold ${c.slug === mainSlug ? "text-white/80" : "text-[#9b9280]"}`}>
                        {mainCounts[c.slug] ?? 0}
                      </span>
                    </Chip>
                  );
                })}
              </div>

              {subTypeOptions.length > 0 && (
                <div className="flex gap-1.5 overflow-x-auto pb-0.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  <Chip active={subSlug === "all"} onClick={() => setSubSlug("all")} className="!py-1 !text-[10.5px]">
                    Tất cả loại
                  </Chip>
                  {subTypeOptions.map(({ slug, name }) => (
                    <Chip
                      key={slug}
                      active={subSlug === slug}
                      onClick={() => setSubSlug(slug)}
                      className="!py-1 !text-[10.5px]"
                    >
                      {name}
                    </Chip>
                  ))}
                </div>
              )}

              <div className="flex items-center justify-between gap-2">
                <p className="text-[11px] font-semibold text-[#7a7467]">
                  {loading ? (
                    "Đang tải..."
                  ) : (
                    <>
                      <span className="font-extrabold text-[#2f352c]">{filtered.length}</span> địa điểm
                    </>
                  )}
                </p>
                <div className="flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => setOnlyFavorites((v) => !v)}
                    aria-pressed={onlyFavorites}
                    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold transition ${
                      onlyFavorites
                        ? "border-[#e8b9b3] bg-[#fdeeeb] text-[#a85d5d]"
                        : "border-[#d9ccb4] bg-white text-[#7a7467] hover:border-[#e8b9b3] hover:text-[#a85d5d]"
                    }`}
                  >
                    <Heart size={12} fill={onlyFavorites ? "currentColor" : "none"} />
                    Yêu thích
                  </button>
                  {hasActiveFilter && (
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="rounded-full px-2 py-1 text-[11px] font-bold text-[#5f745f] underline-offset-2 hover:underline"
                    >
                      Xoá lọc
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* List */}
            <div className="flex-1 overflow-y-auto overscroll-contain px-3 py-3 [scrollbar-gutter:stable]">
              {loading ? (
                <div className="space-y-2.5">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <LocationCardSkeleton key={i} />
                  ))}
                </div>
              ) : error ? (
                <div className="px-4 py-8 text-center text-sm text-red-600">{error}</div>
              ) : filtered.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-[#d3c4a6] bg-[#fffaf0] px-4 py-10 text-center">
                  <MapPin size={28} className="mx-auto mb-2 text-[#b5a78c]" />
                  <p className="font-faustina text-base font-bold text-[#2f352c]">Không tìm thấy địa điểm</p>
                  <p className="mt-1 text-xs text-[#7a7467]">Thử đổi từ khoá hoặc bỏ bớt bộ lọc.</p>
                  {hasActiveFilter && (
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="mt-3 rounded-full bg-[#5f745f] px-4 py-1.5 text-xs font-bold text-white hover:bg-[#4f6250]"
                    >
                      Xoá bộ lọc
                    </button>
                  )}
                </div>
              ) : (
                <div className="space-y-2.5">
                  {filtered.map((item) => (
                    <LocationCard
                      key={item.id}
                      item={item}
                      onSelectCard={selectCardOnMap}
                      isFav={typeof isFavorite === "function" ? isFavorite(item.id) : false}
                      onFav={toggleFavorite}
                    />
                  ))}
                </div>
              )}
            </div>
          </aside>
        )}

        {/* Map */}
        <div className="relative flex flex-1 flex-col overflow-hidden">
          <div className="absolute left-3 top-3 z-[1000] flex items-center gap-2">
            {!hideSidebar && !sidebarOpen && (
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="flex items-center gap-2 rounded-xl border border-[#d8d1c3] bg-[#fffdf8]/95 px-3 py-2 text-xs font-semibold text-[#224d1a] shadow-[0_10px_24px_rgba(0,0,0,0.08)] backdrop-blur-sm transition hover:bg-white"
              >
                <SlidersHorizontal size={14} />
                <span className="hidden sm:inline">Danh sách</span>
                {!loading && (
                  <span className="rounded-full bg-[#224d1a] px-1.5 py-0.5 text-[10px] font-bold text-white">
                    {filtered.length}
                  </span>
                )}
              </button>
            )}
          </div>

          {loading ? (
            <div className="flex h-full items-center justify-center">
              <div className="flex flex-col items-center gap-3">
                <Loader2 className="animate-spin text-[#224d1a]" size={36} />
                <p className="text-sm font-medium text-gray-400">Đang tải bản đồ...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex h-full items-center justify-center">
              <p className="text-sm font-medium text-red-600">{error}</p>
            </div>
          ) : (
            <Gmap
              filteredData={filtered}
              fullScreen
              flyToRequest={flyToRequest}
              isFavorite={isFavorite}
              toggleFavorite={toggleFavorite}
              introPhase={introPhase}
              preferDensestOnLoad={preferDensestOnLoad}
              invalidateSizeKey={`${hideSidebar ? "hidden" : sidebarOpen ? "open" : "closed"}:${filtered.length}`}
            />
          )}
        </div>
      </div>
    </div>
  );
}

